/**
 * @component LinkButton
 * @description shadcn/ui 기반 컴포넌트. CSS 변수를 활용한 테마 시스템 적용.
 * @spec Based on shadcn/ui + Figma design specification
 */

import { forwardRef, ReactNode, AnchorHTMLAttributes } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../lib/utils';

export type LinkButtonVariant = 'primary' | 'secondary' | 'muted';
export type LinkButtonSize = 'small' | 'regular' | 'large';

export interface LinkButtonProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  variant?: LinkButtonVariant;
  size?: LinkButtonSize;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  disabled?: boolean;
}

const linkButtonVariants = cva(
  'inline-flex items-center gap-1.5 font-medium transition-colors underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  {
    variants: {
      variant: {
        primary: 'text-primary hover:text-primary/90',
        secondary: 'text-foreground hover:text-foreground/80',
        muted: 'text-muted-foreground hover:text-foreground',
      },
      size: {
        small: 'text-xs [&>svg]:w-3 [&>svg]:h-3',
        regular: 'text-sm [&>svg]:w-4 [&>svg]:h-4',
        large: 'text-base [&>svg]:w-5 [&>svg]:h-5',
      },
      disabled: {
        true: 'pointer-events-none opacity-50',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'regular',
      disabled: false,
    },
  }
);

export const LinkButton = forwardRef<HTMLAnchorElement, LinkButtonProps>(
  ({ className, variant, size, leftIcon, rightIcon, disabled, children, ...props }, ref) => {
    return (
      <a
        ref={ref}
        className={cn(linkButtonVariants({ variant, size, disabled }), className)}
        aria-disabled={disabled}
        {...props}
      >
        {leftIcon}
        {children}
        {rightIcon}
      </a>
    );
  }
);

LinkButton.displayName = 'LinkButton';
